import React, { useMemo, useState } from "react";
import { Col, Select, Input, Card, Row, Button, Checkbox } from "antd";
import { ArrowDropDown, ArrowDropUp, UploadFile } from '@mui/icons-material';
import { RestartAlt } from '@mui/icons-material';
import styled from "styled-components";
import {
  allCategories,
  allTenseData,
  getLabels,
  moodsOption,
  pronounOptions,
  question_type_generate_more_worksheet,
  reorderOptions,
} from "../helper";
import BadgeButton from "components/AdminBooks/BadgeButton";
import Loading from "components/Loading";
import SentryWrap from 'components/Sentry';

const BarsLoader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
  height: 10px;
  gap: 3px;
  position: absolute;
  right: 17px;

  span {
    width: 4px;
    background: #722ed1;
    animation: barAnim 0.8s infinite;
  }

  span:nth-child(1) { animation-delay: 0s; }
  span:nth-child(2) { animation-delay: 0.1s; }
  span:nth-child(3) { animation-delay: 0.2s; }

  @keyframes barAnim {
    0%, 100% { height: 6px; opacity: 0.3; }
    50% { height: 12px; opacity: 1; }
  }
`;

const ResetButton = styled(Button)`
  height: 40px;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1.5px solid #d9d9d9;
  background: #ffffff;
  color: #595959;
  transition: all 0.3s ease;

  &:hover {
    border-color: #042f73;
    color: #042f73;
    background: #f0f7ff;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(4, 47, 115, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px rgba(4, 47, 115, 0.1);
  }

  .anticon {
    font-size: 18px;
  }
`;

const FilterCard = ({
  search_by_name,
  setSearchByName,
  search_by_tense,
  setSearchByTense,
  search_by_questionType,
  setSearchByQuestionType,
  search_by_category,
  setSearchByCategory,
  searchByPronouns,
  setSearchByPronouns,
  selectedmoodOption,
  setMoodOption,
  isCountPreview = false,
  sortByCount,
  setSortByCount,
  loading = false,
  bugFilter,
  setBugFilter,
}) => {

  const allTenseOption = useMemo(() => {
    return allTenseData();
  }, [moodsOption]);

  const allCategoryOption = useMemo(() => {
    return allCategories();
  }, [moodsOption]);

  const handleReset = () => {
    setSearchByName("");
    setSearchByQuestionType([]);
    setSearchByCategory([]);
    setSearchByPronouns([]);
    setSearchByTense([]);
    setMoodOption([]);
    if (setBugFilter) {
      setBugFilter(false);
    }
    if (setSortByCount) {
      setSortByCount(null);
    }
  };

  const [dropdownVisibility, setDropdownVisibility] = useState({
    mood: false,
    tenses: false,
    categories: false,
    pronouns: false,
    questionType: false,
    questionLength: false,
  });

  const smileIcon = "";

  const renderFormField = (label, state, setState, options, mode) => {
    const modifiedOptions = options.map((option) => {
      const moodSelected = getLabels(selectedmoodOption, moodsOption)?.toString();

      if (option.label === "yo" && moodSelected === "Imperative") {
        return { ...option, disabled: true };
      }

      return { ...option, disabled: false };
    });

    const isMultiple = mode === "multiple";

    const reorderedOptions = reorderOptions(modifiedOptions, state);

    const handleDropdownVisibleChange = (visible, currentLabel) => {
      setDropdownVisibility((prevVisibility) => ({
        ...prevVisibility,
        [currentLabel]: visible,
      }));
    };

    return (
      <Col xs={24} sm={12} md={8} lg={4} style={{ position: "relative" }}>
        {mode === "multiple" && (
          <div
            onClick={() =>
              handleDropdownVisibleChange(!dropdownVisibility[label], label)
            }
            style={{
              position: "absolute",
              bottom: -2,
              right: 8,
              zIndex: 9,
              cursor: "pointer",
            }}
          >
            {dropdownVisibility[label] ? (
              <ArrowDropUp
                style={{ color: "rgba(0, 0, 0, 0.54)", fontSize: 24 }}
              />
            ) : (
              <ArrowDropDown
                style={{ color: "rgba(0, 0, 0, 0.54)", fontSize: 24 }}
              />
            )}
          </div>
        )}

        <div>
          <label style={{ display: "block" }}>{label}</label>
          <Select
            suffixIcon={
              mode === "single" ? (
                dropdownVisibility[label] ? (
                  <ArrowDropUp
                    style={{
                      color: "rgba(0, 0, 0, 0.54)",
                      fontSize: 24,
                      zIndex: 9,
                      position: "absolute",
                      right: -11,
                      top: -7,
                    }}
                  />
                ) : (
                  <ArrowDropDown
                    style={{
                      color: "rgba(0, 0, 0, 0.54)",
                      fontSize: 24,
                      zIndex: 9,
                      position: "absolute",
                      right: -11,
                      top: -7,
                    }}
                  />
                )
              ) : (
                smileIcon
              )
            }
            showSearch={!isMultiple}
            variant="borderless"
            mode={mode === "multiple" ? "multiple" : undefined}
            style={{ width: "100%" }}
            options={reorderedOptions}
            placeholder={`Select ${label}`}
            value={state}
            maxTagCount="responsive"
            onChange={(selected) => setState(isMultiple ? selected : selected)}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            onDropdownVisibleChange={(e) => handleDropdownVisibleChange(e, label)}
            open={dropdownVisibility[label]}
          />
        </div>
      </Col>
    );
  };

  return (
    <SentryWrap>
      <Card type="inner" title="Filters">
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card style={{ width: "100%" }}>
            <Row gutter={[16, 16]} className="items-center">
              {!isCountPreview && (
                <Col xs={24} sm={12} md={8} lg={4}>
                  <label style={{ display: "block" }}>Search by question</label>
                  <Input
                    placeholder="Enter Question"
                    bordered={false}
                    style={{ border: "1px solid #042f73" }}
                    onChange={(e) => setSearchByName(e.target.value)}
                    value={search_by_name}
                  />
                </Col>
              )}

              {[
                {
                  label: "Question Type",
                  state: search_by_questionType,
                  setState: setSearchByQuestionType,
                  options: question_type_generate_more_worksheet.map((item) => ({
                    ...item,
                    label: item.label.replace(/_/g, " "),
                  })),
                  mode: "multiple",
                },
                {
                  label: "Mood",
                  state: selectedmoodOption,
                  setState: setMoodOption,
                  options: moodsOption,
                  mode: "multiple",
                },
                {
                  label: "Tense",
                  state: search_by_tense,
                  setState: setSearchByTense,
                  options: allTenseOption,
                  mode: "multiple",
                },
                {
                  label: "Category",
                  state: search_by_category,
                  setState: setSearchByCategory,
                  options: allCategoryOption,
                  mode: "multiple",
                },
                {
                  label: "Pronouns",
                  state: searchByPronouns,
                  setState: setSearchByPronouns,
                  options: pronounOptions,
                  mode: "multiple",
                },
              ].map(({ label, state, setState, options, mode }) =>
                renderFormField(label, state, setState, options, mode)
              )}

              {isCountPreview && (
                <Col xs={24} sm={12} md={8} lg={4} style={{ position: "relative" }}>
                  <div>
                    <label style={{ display: "block" }}>Sort by Count</label>
                    <Select
                      variant="borderless"
                      style={{ width: "100%" }}
                      placeholder="Select sort order"
                      value={sortByCount}
                      onChange={(value) => setSortByCount(value)}
                      options={[
                        { value: "high", label: "High" },
                        { value: "low", label: "Low" },
                      ]}
                      allowClear
                    />
                  </div>
                </Col>
              )}

              {/* <Col
                xs={24}
                sm={12}
                md={8}
                lg={4}
                style={{ paddingTop: 22 }}
                className="adminWorksheetBtn"
              >
                <ResetButton onClick={handleReset} block>
                  <RestartAlt />
                  Reset
                </ResetButton>
              </Col> */}
              <Col xs={24} sm={12} md={8} lg={4}>
              <div className="flex items-center">
                {!isCountPreview && (
                  <div className="flex align-items-center">
                    <Checkbox disabled={loading} style={{ marginRight: 10 }} onChange={(e) => setBugFilter(e.target.checked)} checked={bugFilter} />
                    <label style={{ display: "flex",alignItems: "center", width: "120px" }}> Bug Question</label>
                  </div>
                )}
              <BadgeButton
                onClick={handleReset}
                startIcon={<RestartAlt />}
                showBadge={false}
                size="medium"
                disabled={loading || !search_by_name?.length && !search_by_questionType?.length && !selectedmoodOption?.length && !search_by_tense?.length && !search_by_category?.length && !searchByPronouns?.length && !sortByCount && !bugFilter}
                style={{
                  backgroundColor: 'unset',
                  backgroundImage: 'linear-gradient(90deg, #2563eb 0%, #06b6d4 100%)',
                  borderRadius: 10,
                  height: 42,
                  minWidth: 160
                }}
              >
                Reset
              </BadgeButton>              </div>
              </Col>
              {loading && (
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'end' }}>
                  <BarsLoader>
                    <span></span>
                    <span></span>
                    <span></span>
                  </BarsLoader>
                </div>
              )}
            </Row>
          </Card>
        </Col>
      </Row>
    </Card>
    </SentryWrap>
  );
};

export default FilterCard;
